import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { parseMatchTime } from "../../utils/dateUtils";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";
import AdminAlerts from "./AdminAlerts";

const API_URL = getBaseURL();

const DashboardInner = () => {
  const [stats, setStats] = useState({ users: 0, contests: 0 });
  const [matchData, setMatchData] = useState({ team1: "", team2: "", start_time: "", format: "T20", venue: "" });
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [syncingCache, setSyncingCache] = useState(false);
  const [creatingMatch, setCreatingMatch] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { matchId, t1, t2 }
  const [deleting, setDeleting] = useState(false);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) {
      navigate("/winrox11-super-admin");
    } else {
      fetchDashboardData();
      fetchMatches();
      fetchTeams();
    }
  }, [token, navigate]);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/teams`, { headers: { Authorization: `Bearer ${token}` } });
      setTeams(res.data || []);
    } catch (err) { /* silently fail */ }
  };

  // Build logo map: shortCode -> logo_url
  const logoMap = teams.reduce((acc, t) => { acc[(t.short_code || "").toUpperCase()] = t.logo_url; return acc; }, {});

  const fetchDashboardData = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/winrox11-super-admin");
    }
  };

  const fetchMatches = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/matches/live-upcoming`, { headers: { Authorization: `Bearer ${token}` } });
      setMatches(res.data);
    } catch (err) { /* silently fail */ }
  };

  const handleClearCache = async () => {
    setSyncingCache(true);
    try {
      const res = await axios.post(`${API_URL}/admin/clear-cache`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || "App data synced successfully!");
      fetchMatches();
      fetchDashboardData();
    } catch (err) {
      toast.error("Sync failed: " + (err.response?.data?.error || err.message));
    } finally { setSyncingCache(false); }
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    if (!matchData.team1.trim() || !matchData.team2.trim()) { toast.warn("Both team codes are required."); return; }
    if (!matchData.start_time) { toast.warn("Match date & time is required."); return; }
    setCreatingMatch(true);
    try {
      const payload = {
        ...matchData,
        team1: matchData.team1.trim().toUpperCase(),
        team2: matchData.team2.trim().toUpperCase(),
      };
      const res = await axios.post(`${API_URL}/admin/matches`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Match ${payload.team1} vs ${payload.team2} created! Contests auto-generated.`);
      setMatchData({ team1: "", team2: "", start_time: "", format: "T20", venue: "" });
      fetchMatches();
    } catch (err) {
      toast.error("Failed to create match: " + (err.response?.data?.error || err.message));
    } finally { setCreatingMatch(false); }
  };

  const handleDeleteMatch = (matchId, t1, t2) => {
    setDeleteConfirm({ matchId, t1, t2 });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/admin/matches/${deleteConfirm.matchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ Match ${deleteConfirm.t1} vs ${deleteConfirm.t2} deleted!`);
      setDeleteConfirm(null);
      fetchMatches();
      fetchDashboardData();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Delete failed: " + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllMatches = async () => {
    if (!window.confirm("⚠️ This will permanently delete ALL matches, players, contests and related data.\n\nAre you absolutely sure?")) return;
    if (!window.confirm("🔴 FINAL WARNING: This cannot be undone. Delete everything?")) return;
    try {
      await axios.delete(`${API_URL}/admin/matches/all`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("All matches cleared. You can now add matches manually.");
      fetchMatches();
      fetchDashboardData();
    } catch (err) {
      toast.error("Failed to clear matches: " + (err.response?.data?.error || err.message));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/winrox11-super-admin");
  };

  const liveMatches = matches.filter(m => m.status === "live" || (m.start_time && parseMatchTime(m) <= new Date() && m.status !== "completed"));
  const upcomingMatches = matches.filter(m => m.status !== "completed" && m.status !== "live" && parseMatchTime(m) > new Date());
  const completedMatches = matches.filter(m => m.status === "completed");

  const controls = [
    { to: "/winrox11-super-admin/manage-teams", icon: "🏏", label: "Teams DB", color: COLORS.indigo },
    { to: "/winrox11-super-admin/manage-contests", icon: "🏆", label: "Contest Templates", color: COLORS.purple },
    { to: "/winrox11-super-admin/completed-matches", icon: "🏁", label: "Completed", color: COLORS.textMuted },
    { to: "/winrox11-super-admin/manage-player-templates", icon: "🎴", label: "Player Templates", color: "#ec4899" },
    { to: "/winrox11-super-admin/revenue", icon: "📊", label: "Revenue", color: COLORS.green },
    { to: "/winrox11-super-admin/goa-leaderboard", icon: "🏖️", label: "Goa Trip", color: COLORS.amber },
    { to: "/winrox11-super-admin/users", icon: "👤", label: "Users", color: "#a78bfa", badge: Number(stats.new_users_today) > 0 },
    { to: "/winrox11-super-admin/user-activity", icon: "🕵️", label: "User Activity", color: "#6366f1" },
    { to: "/winrox11-super-admin/manage-deposits", icon: "💰", label: "Deposits", color: "#34d399", badge: Number(stats.pending_deposits) > 0 },
    { to: "/winrox11-super-admin/manage-withdrawals", icon: "💸", label: "Withdrawals", color: COLORS.red, badge: Number(stats.pending_withdrawals) > 0 },
    {to: "/winrox11-super-admin/app-settings", icon: "📱", label: "App Update", color: "#f472b6"},
    {to: "/winrox11-super-admin/manage-banners", icon: "🖼️", label: "Ads/Banners", color: COLORS.amber},
    {to: "/winrox11-super-admin/manage-admins", icon: "🛡️", label: "Manage Admins", color: "#6366f1"},
    {to: "/winrox11-super-admin/winner-control", icon: "🎭", label: "Ghost Winner", color: "#a855f7"},
  ];


  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>

      {/* TOP BAR */}
      <div style={S.topBar}>
        <div>
          <div style={{ ...S.pageTitle, display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ background: COLORS.indigo + "20", border: `1px solid ${COLORS.indigo}30`, padding: "8px 14px", borderRadius: "12px", fontSize: "14px", fontWeight: 700, color: COLORS.indigoLight, letterSpacing: "0.5px" }}>
              ADMIN
            </span>
            WinroX11 Panel
          </div>
          <div style={S.pageSubtitle}>System Overview & Control Center</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <AdminAlerts />
          <button
            onClick={handleLogout}
            className="admin-btn-hover"
            style={{ ...S.btnCancel, transition: "all 0.15s" }}
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* STATS CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "32px" }}>
        {[
          { label: "Total Matches", value: matches.length, icon: "🏏", color: COLORS.indigo },
          { label: "Live Now", value: liveMatches.length, icon: "⚡", color: COLORS.green, pulse: true },
          { label: "Active Contests", value: Number(stats.contests) || 0, icon: "🏆", color: COLORS.purple },
          { label: "Total Users", value: Number(stats.users) || 0, icon: "👥", color: COLORS.amber },
        ].map(stat => (
          <div
            key={stat.label}
            style={{
              background: COLORS.card,
              border: `1px solid ${stat.color}20`,
              borderRadius: "16px",
              padding: "22px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "absolute", right: "18px", top: "50%", transform: "translateY(-50%)", width: "44px", height: "44px", borderRadius: "12px", background: stat.color + "15", border: `1px solid ${stat.color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>
              {stat.icon}
            </div>
            <div style={{ fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, letterSpacing: "2px", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
              {stat.pulse && liveMatches.length > 0 && (
                <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: COLORS.green, animation: "pulse 1.5s infinite", display: "inline-block" }} />
              )}
              {stat.label}
            </div>
            <div style={{ fontSize: "36px", fontWeight: 800, color: stat.color, marginTop: "8px", letterSpacing: "-1px" }}>
              {stat.value.toLocaleString("en-IN")}
            </div>
          </div>
        ))}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.red}40`, borderRadius: 24, padding: 32, maxWidth: 400, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🗑️</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>Delete Match?</div>
            <div style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 24 }}>
              <strong style={{ color: COLORS.text }}>{deleteConfirm.t1} vs {deleteConfirm.t2}</strong><br />
              This will permanently remove all players, teams, and contests for this match.
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: "12px", background: COLORS.inputBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 12, color: COLORS.textMuted, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} style={{ flex: 1, padding: "12px", background: COLORS.red, border: "none", borderRadius: 12, color: "white", fontWeight: 800, cursor: deleting ? "not-allowed" : "pointer", fontSize: 13, opacity: deleting ? 0.7 : 1 }}>
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MASTER CONTROLS */}
      <div style={S.sectionTitle}>Master Controls</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "12px", marginBottom: "32px" }}>
        {controls.map(c => (
          <Link
            key={c.to}
            to={c.to}
            style={{
              background: COLORS.card,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: "14px",
              padding: "22px 12px",
              textAlign: "center",
              cursor: "pointer",
              textDecoration: "none",
              color: COLORS.text,
              display: "block",
              position: "relative",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = c.color + "60"; e.currentTarget.style.background = c.color + "08"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.cardBorder; e.currentTarget.style.background = COLORS.card; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            {/* 🔴 NOTIFICATION DOT */}
            {c.badge && (
              <span style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                width: "8px",
                height: "8px",
                background: c.color,
                borderRadius: "50%",
                boxShadow: `0 0 10px ${c.color}`,
                animation: "pulse 1.5s infinite"
              }} />
            )}
            <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: c.color + "15", border: `1px solid ${c.color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", margin: "0 auto 10px" }}>
              {c.icon}
            </div>
            <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: COLORS.textMid }}>
              {c.label}
            </div>
          </Link>
        ))}
      </div>

      {/* SYNC CONTROLS */}
      <div style={S.sectionTitle}>Sync Admin Data to App</div>
      <div style={{ display: "flex", gap: "12px", marginBottom: "32px", flexWrap: "wrap" }}>
        <button
          disabled={syncingCache}
          onClick={handleClearCache}
          className={syncingCache ? "" : "admin-btn-hover"}
          style={{ flex: 2, padding: "14px", background: syncingCache ? COLORS.card : COLORS.indigo, border: `1px solid ${syncingCache ? COLORS.cardBorder : COLORS.indigo + "80"}`, borderRadius: "12px", color: "white", fontWeight: 700, fontSize: "14px", cursor: syncingCache ? "not-allowed" : "pointer", opacity: syncingCache ? 0.7 : 1, textTransform: "uppercase", letterSpacing: "1px", transition: "all 0.15s" }}
        >
          {syncingCache ? "⌛ Syncing..." : "🔄 Refresh App Data (Sync Admin to App)"}
        </button>
        <button
          onClick={handleDeleteAllMatches}
          className="admin-btn-hover"
          style={{ flex: 1, padding: "14px", background: COLORS.red + "15", border: `1px solid ${COLORS.red}40`, borderRadius: "12px", color: COLORS.red, fontWeight: 700, fontSize: "13px", cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.5px", transition: "all 0.15s" }}
        >
          🗑️ Clear All Matches
        </button>
      </div>

      {/* CREATE MATCH FORM */}
      <div style={S.card}>
        <div style={S.cardTitle}>➕ Schedule New Match</div>
        <form onSubmit={handleCreateMatch}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={S.label}>Team A *</label>
              <select className="admin-input-focus" style={S.select} value={matchData.team1} onChange={e => setMatchData({ ...matchData, team1: e.target.value })} required>
                <option value="">-- Select Team A --</option>
                {teams.map(t => <option key={t.short_code} value={t.short_code}>{t.name} ({t.short_code})</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Team B *</label>
              <select className="admin-input-focus" style={S.select} value={matchData.team2} onChange={e => setMatchData({ ...matchData, team2: e.target.value })} required>
                <option value="">-- Select Team B --</option>
                {teams.filter(t => t.short_code !== matchData.team1).map(t => <option key={t.short_code} value={t.short_code}>{t.name} ({t.short_code})</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={S.label}>Match Format</label>
              <select className="admin-input-focus" style={S.select} value={matchData.format} onChange={e => setMatchData({ ...matchData, format: e.target.value })}>
                <option value="T20">T20</option>
                <option value="ODI">ODI</option>
                <option value="T10">T10</option>
                <option value="TEST">TEST</option>
                <option value="IPL">IPL</option>
              </select>
            </div>
            <div>
              <label style={S.label}>Date & Time *</label>
              <input className="admin-input-focus" type="datetime-local" style={S.input} value={matchData.start_time} onChange={e => setMatchData({ ...matchData, start_time: e.target.value })} required />
            </div>
            <div>
              <label style={S.label}>Venue (optional)</label>
              <input className="admin-input-focus" style={S.input} placeholder="e.g. Wankhede Stadium" list="venues-list" value={matchData.venue} onChange={e => setMatchData({ ...matchData, venue: e.target.value })} />
              <datalist id="venues-list">
                {["Wankhede Stadium, Mumbai", "Eden Gardens, Kolkata", "M. Chinnaswamy Stadium, Bengaluru", "Narendra Modi Stadium, Ahmedabad", "MA Chidambaram Stadium, Chennai", "Rajiv Gandhi International Stadium, Hyderabad", "Arun Jaitley Stadium, Delhi", "Punjab Cricket Association Stadium, Mohali", "Sawai Mansingh Stadium, Jaipur", "JSCA International Stadium, Ranchi", "Bharat Ratna Shri Atal Bihari Vajpayee Ekana Stadium, Lucknow", "DY Patil Stadium, Navi Mumbai"].map(v => <option key={v} value={v} />)}
              </datalist>
            </div>
          </div>
          <div style={S.btnRow}>
            <button
              type="button"
              style={S.btnCancel}
              onClick={() => setMatchData({ team1: "", team2: "", start_time: "", format: "T20", venue: "" })}
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={creatingMatch}
              className={creatingMatch ? "" : "admin-btn-hover"}
              style={{ ...S.btnPrimary, opacity: creatingMatch ? 0.7 : 1, cursor: creatingMatch ? "not-allowed" : "pointer", transition: "all 0.15s" }}
            >
              {creatingMatch ? "Creating..." : "🚀 Publish Match"}
            </button>
          </div>
        </form>
      </div>

      {/* UPCOMING & LIVE MATCHES */}
      {[...liveMatches, ...upcomingMatches].length > 0 && (
        <>
          <div style={S.sectionTitle}>Upcoming & Live Matches ({[...liveMatches, ...upcomingMatches].length})</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "14px", marginBottom: "32px" }}>
            {[...liveMatches.sort((a, b) => parseMatchTime(a) - parseMatchTime(b)), ...upcomingMatches.sort((a, b) => parseMatchTime(a) - parseMatchTime(b))].map(m => {
              const isLive = m.status === "live" || (parseMatchTime(m) <= new Date() && m.status !== "completed");
              const statusLabel = isLive ? "live" : (m.status || "upcoming");
              const statusColor = isLive ? COLORS.green : COLORS.indigo;
              return (
                <div
                  key={m.match_id}
                  style={{
                    background: COLORS.card,
                    border: `1px solid ${isLive ? COLORS.green + "30" : COLORS.cardBorder}`,
                    borderRadius: "16px",
                    padding: "18px",
                    position: "relative",
                    transition: "all 0.15s",
                  }}
                  className="admin-card-hover"
                >
                  <div style={{ position: "absolute", top: "12px", right: "12px", display: "flex", gap: "4px" }}>
                    <button style={{ ...S.miniBtn, color: COLORS.red }} onClick={() => handleDeleteMatch(m.match_id, m.team1, m.team2)}>🗑️</button>
                  </div>

                  <div style={{ marginBottom: "12px" }}>
                    <span style={{ ...S.badge(statusColor), display: "inline-flex", alignItems: "center", gap: "5px" }}>
                      {isLive && <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: COLORS.green, animation: "pulse 1.5s infinite", display: "inline-block" }} />}
                      {statusLabel.toUpperCase()}
                    </span>
                    <span style={{ ...S.badge(COLORS.textMuted), marginLeft: "8px" }}>{m.format || "T20"}</span>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {logoMap[(m.team1 || "").toUpperCase()] ? (
                        <img src={logoMap[(m.team1 || "").toUpperCase()]} alt={m.team1} style={{ width: 36, height: 36, borderRadius: 8, objectFit: "contain", background: COLORS.inputBg, padding: 2 }} onError={e => { e.target.style.display = "none"; }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: COLORS.indigo + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: COLORS.indigoLight }}>{(m.team1 || "??").substring(0, 2)}</div>
                      )}
                      <div>
                        <div style={{ fontSize: "20px", fontWeight: 800, color: COLORS.text }}>{m.team1_short || m.team1?.substring(0, 3)}</div>
                        <div style={{ fontSize: "9px", color: COLORS.textMuted, fontWeight: 600 }}>{m.team1}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: "12px", color: COLORS.textDim, fontWeight: 600, fontStyle: "italic" }}>VS</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, textAlign: "right", flexDirection: "row-reverse" }}>
                      {logoMap[(m.team2 || "").toUpperCase()] ? (
                        <img src={logoMap[(m.team2 || "").toUpperCase()]} alt={m.team2} style={{ width: 36, height: 36, borderRadius: 8, objectFit: "contain", background: COLORS.inputBg, padding: 2 }} onError={e => { e.target.style.display = "none"; }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: COLORS.green + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: COLORS.green }}>{(m.team2 || "??").substring(0, 2)}</div>
                      )}
                      <div>
                        <div style={{ fontSize: "20px", fontWeight: 800, color: COLORS.text }}>{m.team2_short || m.team2?.substring(0, 3)}</div>
                        <div style={{ fontSize: "9px", color: COLORS.textMuted, fontWeight: 600 }}>{m.team2}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ fontSize: "11px", color: COLORS.textMuted, marginBottom: "14px" }}>
                    📅 {parseMatchTime(m).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    {m.venue && <span style={{ marginLeft: "8px" }}>🏟️ {m.venue}</span>}
                  </div>

                  <Link
                    to={`/winrox11-super-admin/match/${m.match_id}`}
                    style={{
                      display: "block",
                      padding: "10px",
                      background: isLive ? COLORS.green + "15" : COLORS.indigo + "15",
                      border: `1px solid ${isLive ? COLORS.green + "30" : COLORS.indigo + "30"}`,
                      borderRadius: "10px",
                      color: isLive ? COLORS.green : COLORS.indigoLight,
                      fontWeight: 700,
                      fontSize: "11px",
                      textDecoration: "none",
                      textAlign: "center",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      transition: "all 0.15s",
                    }}
                  >
                    {isLive ? "🔴 Open Live Panel" : "⚙️ Open Match Panel"}
                  </Link>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* No redundant completed matches list here, moved to separate page */}
    </div>
  );
};

const AdminDashboard = () => (
  <ToastProvider>
    <DashboardInner />
  </ToastProvider>
);

export default AdminDashboard;
